import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WidgetAuthErrorEnum } from './models/widget-auth-error.enum';
import { AnalyticResponseModel } from './models/analytic-response.model';
import { DeveloperAnalyticModel } from './models/developer-analytic.model';
import { UserAnalyticModel } from './models/user-analytic.model';
import { plainToClass } from 'class-transformer';
import { WidgetStateModel } from './state/widget.state.model';
import { WidgetInteractionEnum } from './models/widget-interaction.enum';
import { WidgetPlatformEnum } from './models/widget-platform.enum';
import { env } from 'process';
import { environment } from 'src/environments/environment';
import { stringify } from 'qs';

const formHeaders = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class WidgetAnalyticsService {
  constructor(private http: HttpClient) {}

  /**
   * Validates the specified analytic id.
   */
  getAnalytic(analyticId: string): Observable<AnalyticResponseModel> {
    if (environment.offline) {
      fetch('https://human.biodigital.com/ws/user/analytics?analytic_id=' + analyticId, {
        method: 'GET',
        mode: 'no-cors',
      });
      return of({ success: true });
    }
    const url = '/ws/user/analytics?analytic_id=' + analyticId;
    return this.http.get(url).pipe(
      map(() => ({ success: true })),
      catchError((response: HttpErrorResponse) => of(this.parseError(response))),
    );
  }

  /**
   * Sends an analytic and return the validation status.
   */
  sendAnalytic(
    analytic: UserAnalyticModel | DeveloperAnalyticModel,
  ): Observable<AnalyticResponseModel> {
    if (environment.offline) {
      // url encode the analytics object and POST using no-cors fetch
      const url =
        analytic instanceof UserAnalyticModel
          ? 'https://human.biodigital.com/ws/user/analytics'
          : 'https://human.biodigital.com/ws/developer/apps/access/key';
      const json = JSON.parse(JSON.stringify(analytic));
      const encoded = stringify(json);
      fetch(url, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        method: 'POST',
        mode: 'no-cors',
        body: encoded,
      });
      return of({ success: true });
    }
    const request$ =
      analytic instanceof UserAnalyticModel
        ? // user analytic
          this.http.post('/ws/user/analytics', analytic, formHeaders)
        : // developer analytic
          this.http.post('/ws/developer/apps/access/key', analytic, formHeaders);

    return request$.pipe(
      map(() => ({ success: true })),
      catchError((response: HttpErrorResponse) => of(this.parseError(response))),
    );
  }

  buildDeveloperAnalytic(
    widgetState: WidgetStateModel,
    activity: WidgetInteractionEnum,
    platform = WidgetPlatformEnum.Widget,
    activityData: any,
  ): DeveloperAnalyticModel {
    const { widgetUrlConfig: widgetConfig, parentUrl, embedded } = widgetState;

    const analytic = {
      platform: platform,
      app_activity: activity,
      // app keys
      app_productkey: widgetConfig['app-productkey'],
      app_userid: widgetConfig['app-userid'],
      // user keys
      app_apikey: widgetConfig['dk'],
      // full embbed domain path
      app_domain: parentUrl.full,
      app_activity_data: activityData,
      widget_is_fullscreen: !embedded,
    };

    return plainToClass(DeveloperAnalyticModel, analytic);
  }

  private parseError(response: HttpErrorResponse): AnalyticResponseModel {
    if (environment.offline) {
      // for offline mode we can't connect to the auth server, this is ok
      return { success: true, message: '', errorCode: null };
    }
    const error = response.error.error;
    const status = response.status;
    // server error isn't an auth failure
    const success = status >= 500 && status <= 504;
    const message = error && error.message ? error.message : '';
    const errorCode = Object.values(WidgetAuthErrorEnum).includes(message)
      ? <WidgetAuthErrorEnum>message
      : null;
    return { success: success, message: message, errorCode: errorCode };
  }
}
